import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  BarChart3,
  Clock3,
  Filter,
  Lightbulb,
  Loader2,
  Pause,
  RotateCcw,
  Users,
} from "lucide-react";
import ReactSelect, { SingleValue, StylesConfig } from "react-select";
import { toast } from "react-toastify";
import PageHeader from "../../../components/PageHeader";
import { SELECT_STYLES } from "../../../components/DateFilters/constants";
import { useAuth } from "../../../context/AuthContext";
import {
  canVerTodasLojasUser,
  hasFullAccessUser,
} from "../../../lib/screenAccess";
import {
  controleProducaoService,
  PausaFichaItem,
  PausaPeriodoItem,
  RelatorioAssinaturasProducaoResponse,
  RelatorioTemposProducaoUnd,
  RelatorioTemposRankingLinha,
  RelatorioTemposResponse,
} from "../../../services/controleProducaoService";
import { lojaService } from "../../../services/lojaService";
import { Loja } from "../../../types/loja";
import { BrazilDate } from "../../../utils/BrazilDate";
import { aplicarFiltrosRelatorioTempos } from "./filtrosRelatorioTempos";
import RankingList from "./RankingList";
import InsightsModal from "./InsightsModal";
import RelatorioTemposCharts from "./RelatorioTemposCharts";
import EntregaSetorSection from "./EntregaSetorSection";
import PontuacaoTab from "./PontuacaoTab";
import MetaAssinaturaTab from "./MetaAssinaturaTab";
import {
  CabecalhoUndMobile,
  CardMetrica,
  CardMetricas,
  ListaMobile,
  TabelaDesktop,
} from "./tabelaResponsiva";
import {
  formatNota,
  montarPontuacaoFuncionarios,
} from "./pontuacaoFuncionarios";
import { groupByUndNome, rotuloUnd } from "../RelatorioConsumo/groupByUnd";

function primeiroDiaMesISO(): string {
  const now = new Date();
  return BrazilDate.formatISO(
    BrazilDate.create(now.getFullYear(), now.getMonth() + 1, 1)
  );
}

function hojeISO(): string {
  return BrazilDate.todayISO();
}

type Aba =
  | "rankings"
  | "pontuacao"
  | "meta16h"
  | "setores"
  | "itens"
  | "usuarios"
  | "detalhe"
  | "pausas";

const ABAS: { id: Aba; label: string }[] = [
  { id: "rankings", label: "Rankings" },
  { id: "pontuacao", label: "Pontuação" },
  { id: "meta16h", label: "Meta 16h (sáb. 17h)" },
  { id: "setores", label: "Por setor" },
  { id: "itens", label: "Por item" },
  { id: "usuarios", label: "Por usuário" },
  { id: "detalhe", label: "Detalhamento" },
  { id: "pausas", label: "Pausas" },
];

type LojaOpt = { value: number; label: string };

type AgrupamentoOpt = { value: "dia" | "semana" | "mes"; label: string };

const FILTRO_SELECT_STYLES = {
  ...(SELECT_STYLES as StylesConfig<LojaOpt, false>),
  container: (base) => ({
    ...base,
    width: "100%",
    minWidth: 0,
  }),
  control: (base, state) => ({
    ...(SELECT_STYLES.control?.(base, state) ?? base),
    minHeight: "44px",
    height: "auto",
    maxWidth: "100%",
  }),
  valueContainer: (base) => ({
    ...base,
    minHeight: "42px",
    padding: "0 8px",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    minHeight: "42px",
  }),
} satisfies StylesConfig<LojaOpt, false>;

const LOJA_SELECT_STYLES = FILTRO_SELECT_STYLES as unknown as StylesConfig<
  LojaOpt,
  false
>;

const AGRUPAMENTO_SELECT_STYLES =
  FILTRO_SELECT_STYLES as unknown as StylesConfig<AgrupamentoOpt, false>;

const AGRUPAMENTO_OPTIONS: AgrupamentoOpt[] = [
  { value: "dia", label: "Dia" },
  { value: "semana", label: "Semana" },
  { value: "mes", label: "Mês" },
];

const CAMPO_FILTRO_CLASS =
  "h-11 w-full min-w-0 max-w-full appearance-none rounded-md border border-neutral-300 px-3 text-base md:h-[38px] md:text-sm";

const SELECT_FILTRO_CLASS =
  "mt-1 h-11 w-full min-w-0 rounded-md border border-neutral-300 bg-white px-3 text-base md:h-auto md:py-2 md:text-sm";

function ProducaoPorUnd({
  itens,
  empty = "—",
  align = "end",
}: {
  itens?: RelatorioTemposProducaoUnd[] | null;
  empty?: string;
  align?: "start" | "end";
}) {
  if (!itens?.length) {
    return <span className="text-neutral-400">{empty}</span>;
  }
  return (
    <ul className="space-y-1">
      {itens.map((p) => (
        <li
          key={p.undNome}
          className={`flex items-center gap-2 tabular-nums ${
            align === "end" ? "justify-end" : "justify-start"
          }`}
        >
          <span>
            {p.total.toLocaleString("pt-BR", { maximumFractionDigits: 3 })}
          </span>
          <span className="rounded-full bg-neutral-900 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
            {p.undNome}
          </span>
        </li>
      ))}
    </ul>
  );
}

function CabecalhoUnd({
  undNome,
  count,
  colSpan,
  singular = "item",
  plural = "itens",
}: {
  undNome: string;
  count: number;
  colSpan: number;
  singular?: string;
  plural?: string;
}) {
  return (
    <tr className="border-t border-neutral-200 bg-neutral-100">
      <td
        colSpan={colSpan}
        className="px-3 py-2 text-xs font-bold uppercase tracking-wide text-neutral-700"
      >
        Unidade {undNome}
        <span className="ml-2 font-medium normal-case text-neutral-500">
          {count} {count === 1 ? singular : plural}
        </span>
      </td>
    </tr>
  );
}

const ControleProducaoRelatorioTemposPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = hasFullAccessUser(user);
  const podeVerTodasLojas = canVerTodasLojasUser(user);
  const [lojas, setLojas] = useState<Loja[]>([]);
  const [selectedLojaId, setSelectedLojaId] = useState<number | undefined>(
    user?.lojaId
  );
  const [dataInicio, setDataInicio] = useState(primeiroDiaMesISO);
  const [dataFim, setDataFim] = useState(hojeISO);
  const [agrupamento, setAgrupamento] = useState<"dia" | "semana" | "mes">(
    "dia"
  );
  const [loading, setLoading] = useState(false);
  const [relatorio, setRelatorio] = useState<RelatorioTemposResponse | null>(
    null
  );
  const [assinaturas, setAssinaturas] =
    useState<RelatorioAssinaturasProducaoResponse | null>(null);
  const [aba, setAba] = useState<Aba>("rankings");
  const [pausas, setPausas] = useState<PausaFichaItem[]>([]);
  const [loadingPausas, setLoadingPausas] = useState(false);
  const [pausaFiltroUsuario, setPausaFiltroUsuario] = useState("");
  const [pausaFiltroItem, setPausaFiltroItem] = useState("");
  const [pausaFiltroLongas, setPausaFiltroLongas] = useState(false);
  const [pausaPagina, setPausaPagina] = useState(0);
  const [setorFiltro, setSetorFiltro] = useState<number | "">("");
  const [usuarioFiltro, setUsuarioFiltro] = useState<number | "">("");
  const [itemFiltro, setItemFiltro] = useState<number | "">("");
  const [undFiltro, setUndFiltro] = useState("");
  const [insightsAberto, setInsightsAberto] = useState(false);

  const lojaOptions = useMemo(
    () =>
      podeVerTodasLojas ? lojas : lojas.filter((l) => l.id === user?.lojaId),
    [podeVerTodasLojas, lojas, user?.lojaId]
  );

  const lojaSelectOptions = useMemo<LojaOpt[]>(
    () => lojaOptions.map((loja) => ({ value: loja.id, label: loja.nome })),
    [lojaOptions]
  );

  const selectedLojaOption =
    lojaSelectOptions.find((o) => o.value === selectedLojaId) ?? null;

  const agrupamentoOption =
    AGRUPAMENTO_OPTIONS.find((o) => o.value === agrupamento) ??
    AGRUPAMENTO_OPTIONS[0];

  useEffect(() => {
    void lojaService
      .getLojas()
      .then(setLojas)
      .catch(() => setLojas([]));
  }, []);

  const carregar = useCallback(async () => {
    if (!selectedLojaId) return;
    setLoading(true);
    try {
      const [temposResult, assinResult] = await Promise.allSettled([
        controleProducaoService.getRelatorioTempos({
          dataInicio,
          dataFim,
          lojaId: selectedLojaId,
          agrupamento,
        }),
        controleProducaoService.getRelatorioAssinaturasProducao({
          dataInicio,
          dataFim,
          lojaId: selectedLojaId,
        }),
      ]);
      if (temposResult.status === "fulfilled") {
        setRelatorio(temposResult.value);
      } else {
        setRelatorio(null);
        const message =
          temposResult.reason instanceof Error
            ? temposResult.reason.message
            : "Erro ao gerar relatório.";
        toast.error(message);
      }
      if (assinResult.status === "fulfilled") {
        setAssinaturas(assinResult.value);
      } else {
        setAssinaturas(null);
        if (temposResult.status === "fulfilled") {
          toast.error("Não foi possível carregar a meta de assinatura 16h.");
        }
      }
      setSetorFiltro("");
      setUsuarioFiltro("");
      setItemFiltro("");
      setUndFiltro("");
      setInsightsAberto(false);
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Erro ao gerar relatório.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [selectedLojaId, dataInicio, dataFim, agrupamento]);

  const carregarPausas = useCallback(async () => {
    if (!selectedLojaId) return;
    setLoadingPausas(true);
    try {
      const data = await controleProducaoService.listarPausasPeriodo({
        dataInicio,
        dataFim,
        lojaId: selectedLojaId,
      });
      setPausas(data);
    } catch {
      setPausas([]);
    } finally {
      setLoadingPausas(false);
    }
  }, [selectedLojaId, dataInicio, dataFim]);

  useEffect(() => {
    if (aba === "pausas" && relatorio) {
      void carregarPausas();
    }
  }, [aba, carregarPausas, relatorio]);

  const resumo = relatorio?.resumo;
  const insights = relatorio?.insights ?? [];

  const filtrado = useMemo(() => {
    if (!relatorio) return null;
    return aplicarFiltrosRelatorioTempos(relatorio, {
      setorId: setorFiltro,
      userId: usuarioFiltro,
      itemId: itemFiltro,
      undNome: undFiltro,
    });
  }, [relatorio, setorFiltro, usuarioFiltro, itemFiltro, undFiltro]);

  const setores = filtrado?.setores ?? [];
  const itens = filtrado?.itens ?? [];
  const usuarios = filtrado?.usuarios ?? [];
  const linhas = filtrado?.linhas ?? [];
  const entregaPorSetor = filtrado?.entregaPorSetor ?? [];
  const rankings = filtrado?.rankings;
  const pontuacao = useMemo(
    () =>
      montarPontuacaoFuncionarios(
        relatorio?.porUsuario ?? [],
        undFiltro,
        usuarios
      ),
    [relatorio?.porUsuario, undFiltro, usuarios]
  );
  const rankingPontuacao = useMemo(
    () =>
      pontuacao.ranking.slice(0, 8).map((row) => ({
        posicao: row.posicao,
        id: row.userId,
        nome: row.userNome,
        ocorrencias: row.ocorrencias,
        valor: row.pontuacao,
        valorFormatado: `${formatNota(row.pontuacao)} / 10`,
        detalhe: `Média de ${row.diasComNota} ${row.diasComNota === 1 ? "dia" : "dias"}${
          row.notasDias.length
            ? ` (${row.notasDias.map((n) => formatNota(n)).join(", ")})`
            : ""
        } · ${row.producaoFormatada}`,
      })),
    [pontuacao]
  );

  const setorOptions = relatorio?.porSetor ?? [];
  const usuarioOptions = relatorio?.porUsuario ?? [];
  const itemOptions = useMemo(() => {
    const lista = relatorio?.porItem ?? [];
    return [...lista]
      .filter((item) => !undFiltro || rotuloUnd(item.undNome) === undFiltro)
      .sort((a, b) => a.descricao.localeCompare(b.descricao));
  }, [relatorio, undFiltro]);

  const undOptions = useMemo(() => {
    const nomes = [
      ...(relatorio?.porItem ?? []).map((i) => i.undNome),
      ...(relatorio?.resumo?.producaoPorUnd ?? []).map((p) => p.undNome),
      ...(relatorio?.linhas ?? []).map((l) => l.undNome),
    ];
    return groupByUndNome(
      nomes.map((undNome) => ({ undNome: rotuloUnd(undNome) })),
      (x) => x.undNome
    ).map((g) => g.undNome);
  }, [relatorio]);

  const itensPorUnd = useMemo(
    () => groupByUndNome(itens, (item) => item.undNome),
    [itens]
  );
  const linhasPorUnd = useMemo(
    () => groupByUndNome(linhas, (linha) => linha.undNome),
    [linhas]
  );

  const producaoUndCards = useMemo(() => {
    const lancada = resumo?.producaoPorUnd ?? [];
    const crono = resumo?.producaoCronometradaPorUnd ?? [];
    const nomes = new Set([
      ...lancada.map((p) => p.undNome),
      ...crono.map((p) => p.undNome),
    ]);
    return groupByUndNome(
      Array.from(nomes).map((undNome) => ({ undNome })),
      (x) => x.undNome
    ).map((g) => ({
      undNome: g.undNome,
      lancada: lancada.find((p) => rotuloUnd(p.undNome) === g.undNome),
      cronometrada: crono.find((p) => rotuloUnd(p.undNome) === g.undNome),
    }));
  }, [resumo]);

  const origensProducao = resumo?.producaoPorOrigem ?? [];
  const mostrarOrigensProducao =
    origensProducao.length > 1 ||
    origensProducao.some((origem) => origem.lojaId !== selectedLojaId);

  const temFiltroIndividual =
    setorFiltro !== "" ||
    usuarioFiltro !== "" ||
    itemFiltro !== "" ||
    Boolean(undFiltro);

  const limparFiltros = () => {
    setSetorFiltro("");
    setUsuarioFiltro("");
    setItemFiltro("");
    setUndFiltro("");
  };

  const selecionarRanking =
    (tipo: "usuario" | "item" | "setor") =>
    (row: RelatorioTemposRankingLinha) => {
      if (tipo === "usuario") {
        setUsuarioFiltro(row.id);
        setAba("usuarios");
        return;
      }
      if (tipo === "item") {
        setItemFiltro(row.id);
        setAba("itens");
        return;
      }
      setSetorFiltro(row.id);
      setAba("setores");
    };

  return (
    <main className="mx-auto max-w-7xl overflow-x-hidden px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
      <PageHeader
        title="Desempenho de Produção"
        description="Tempos, quantidades, pontuação da equipe, meta de assinatura até 16h (sábado até 17h) e rankings por usuário, item e setor."
      />

      <div className="mt-4 grid min-w-0 gap-3 rounded-lg border border-neutral-200 bg-white p-3 sm:mt-6 sm:grid-cols-2 sm:items-end sm:p-4 lg:flex lg:flex-row lg:items-end lg:gap-3">
        <div className="flex min-w-0 flex-col gap-1.5 lg:flex-1">
          <label
            htmlFor="relatorio-tempos-loja"
            className="text-sm font-medium leading-tight text-neutral-700"
          >
            Loja
          </label>
          <ReactSelect<LojaOpt, false>
            inputId="relatorio-tempos-loja"
            value={selectedLojaOption}
            onChange={(option: SingleValue<LojaOpt>) =>
              setSelectedLojaId(option?.value)
            }
            options={lojaSelectOptions}
            isClearable={isAdmin}
            placeholder="Selecionar loja"
            styles={LOJA_SELECT_STYLES}
          />
        </div>
        <div className="flex min-w-0 flex-col gap-1.5 lg:flex-1">
          <label
            htmlFor="relatorio-tempos-inicio"
            className="text-sm font-medium leading-tight text-neutral-700"
          >
            De
          </label>
          <input
            id="relatorio-tempos-inicio"
            type="date"
            value={dataInicio}
            onChange={(e) => setDataInicio(e.target.value)}
            className={CAMPO_FILTRO_CLASS}
          />
        </div>
        <div className="flex min-w-0 flex-col gap-1.5 lg:flex-1">
          <label
            htmlFor="relatorio-tempos-fim"
            className="text-sm font-medium leading-tight text-neutral-700"
          >
            Até
          </label>
          <input
            id="relatorio-tempos-fim"
            type="date"
            value={dataFim}
            onChange={(e) => setDataFim(e.target.value)}
            className={CAMPO_FILTRO_CLASS}
          />
        </div>
        <div className="flex min-w-0 flex-col gap-1.5 lg:flex-1">
          <label
            htmlFor="relatorio-tempos-agrupamento"
            className="text-sm font-medium leading-tight text-neutral-700"
          >
            Agrupamento do detalhe
          </label>
          <ReactSelect<AgrupamentoOpt, false>
            inputId="relatorio-tempos-agrupamento"
            value={agrupamentoOption}
            onChange={(option: SingleValue<AgrupamentoOpt>) => {
              if (option) setAgrupamento(option.value);
            }}
            options={AGRUPAMENTO_OPTIONS}
            isSearchable={false}
            styles={AGRUPAMENTO_SELECT_STYLES}
          />
        </div>
        <button
          type="button"
          onClick={() => void carregar()}
          disabled={loading || !selectedLojaId}
          className="h-11 w-full shrink-0 rounded-md bg-primary px-4 text-base font-semibold text-white disabled:opacity-60 sm:col-span-2 md:h-[38px] md:text-sm lg:w-auto lg:min-w-[9.5rem]"
        >
          {loading ? "Gerando..." : "Gerar relatório"}
        </button>
      </div>
      <p className="mt-2 text-xs text-neutral-500">
        Rankings de tempo usam só cronômetro encerrado no turno DIA. O tempo
        pausado (botão ou troca de item) fica separado da produção — não entra
        na média de ritmo, mas aparece no ranking de pausa. Horas de trabalho
        por pessoa ignoram cronômetros sobrepostos (a mesma hora não conta duas
        vezes). Em Birigui, a quantidade inclui itens originais de Araçatuba
        produzidos na ficha DIA nos dias de “Produção em Birigui”. A nota da
        equipe é a média das notas de cada dia do período (0 a 10, o melhor do
        dia vale 10). A aba Meta 16h mede se cada setor assinou PRODUÇÃO até as
        16:00 do dia da ficha — exceto aos sábados, quando o limite é 17:00.
      </p>

      {loading && (
        <p className="mt-4 flex items-center gap-2 text-sm text-neutral-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Calculando desempenho...
        </p>
      )}

      {relatorio && !loading && resumo && (
        <div className="mt-6 min-w-0 space-y-6">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-lg border border-neutral-200 bg-white p-4">
              <p className="flex items-center gap-1.5 text-xs font-medium uppercase text-neutral-500">
                <Clock3 className="h-3.5 w-3.5" />
                Tempo de produção
              </p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-neutral-900">
                {resumo.tempoTotalFormatado}
              </p>
              <p className="mt-0.5 text-xs text-neutral-500">
                Média {resumo.tempoMedioFormatado} por pessoa/dia ·{" "}
                {resumo.cronometragensEncerradas} encerrados
              </p>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <p className="flex items-center gap-1.5 text-xs font-medium uppercase text-amber-800">
                <Pause className="h-3.5 w-3.5" />
                Tempo pausado
              </p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-amber-900">
                {resumo.tempoPausaFormatado ?? "00:00"}
              </p>
              <p className="mt-0.5 text-xs text-amber-800/80">
                Pausa do item, separado da produção
              </p>
            </div>
            <div className="rounded-lg border border-neutral-200 bg-white p-4">
              <p className="flex items-center gap-1.5 text-xs font-medium uppercase text-neutral-500">
                <Users className="h-3.5 w-3.5" />
                Equipe
              </p>
              <p className="mt-1 text-2xl font-bold text-neutral-900">
                {resumo.usuariosDistintos}
              </p>
              <p className="mt-0.5 text-xs text-neutral-500">
                {resumo.setoresDistintos} setores · {resumo.fichasComTempo}{" "}
                fichas
              </p>
            </div>
            <div className="rounded-lg border border-neutral-200 bg-white p-4">
              <p className="flex items-center gap-1.5 text-xs font-medium uppercase text-neutral-500">
                <BarChart3 className="h-3.5 w-3.5" />
                Cobertura
              </p>
              <p className="mt-1 text-2xl font-bold text-neutral-900">
                {resumo.itensProduzidosSemCronometro}
              </p>
              <p className="mt-0.5 text-xs text-neutral-500">
                produções sem cronômetro
                {resumo.itensNaoProduzidosUnidade
                  ? ` · ${resumo.itensNaoProduzidosUnidade} outra unidade`
                  : ""}
              </p>
            </div>
          </div>

          {producaoUndCards.length > 0 && (
            <section>
              <h2 className="mb-2 text-sm font-semibold text-neutral-800">
                Produção por unidade de medida
              </h2>
              <p className="mb-3 text-xs text-neutral-500">
                kg, un e demais unidades ficam em cards separados — não há total
                misturado.
                {mostrarOrigensProducao
                  ? " Em Birigui, a demanda de Araçatuba produzida aqui aparece por loja de origem."
                  : ""}
              </p>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {producaoUndCards.map((card) => (
                  <div
                    key={card.undNome}
                    className="rounded-lg border border-neutral-200 bg-white p-4"
                  >
                    <p className="flex items-center gap-2 text-xs font-medium uppercase text-neutral-500">
                      <span className="rounded-full bg-neutral-900 px-2 py-0.5 text-[10px] font-bold tracking-wide text-white">
                        {card.undNome}
                      </span>
                      Produção
                    </p>
                    <p className="mt-2 text-2xl font-bold tabular-nums text-neutral-900">
                      {card.lancada
                        ? card.lancada.total.toLocaleString("pt-BR", {
                            maximumFractionDigits: 3,
                          })
                        : "—"}{" "}
                      <span className="text-base font-semibold text-neutral-500">
                        {card.undNome}
                      </span>
                    </p>
                    <p className="mt-1 text-xs text-neutral-500">
                      com cronômetro: {card.cronometrada?.totalFormatado ?? "—"}
                    </p>
                    {mostrarOrigensProducao && (
                      <ul className="mt-2 space-y-0.5 text-xs text-neutral-600">
                        {origensProducao.map((origem) => {
                          const qtd = origem.producaoPorUnd.find(
                            (p) => rotuloUnd(p.undNome) === card.undNome
                          );
                          if (!qtd) return null;
                          return (
                            <li
                              key={`${origem.lojaId}-${card.undNome}`}
                              className="flex justify-between gap-2 tabular-nums"
                            >
                              <span className="truncate">
                                {origem.lojaNome}
                              </span>
                              <span className="font-medium text-neutral-800">
                                {qtd.total.toLocaleString("pt-BR", {
                                  maximumFractionDigits: 3,
                                })}{" "}
                                {card.undNome}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {insights.length > 0 && (
            <div className="flex flex-col gap-3 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-4">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-neutral-900">
                  Insights
                </p>
                <p className="text-sm text-neutral-600">
                  {insights.length}{" "}
                  {insights.length === 1 ? "destaque" : "destaques"} do período
                  — ritmos, volumes e pontos de atenção.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setInsightsAberto(true)}
                className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg border border-neutral-300 bg-white px-4 text-sm font-semibold text-neutral-800 shadow-sm hover:bg-neutral-100 sm:h-auto sm:w-auto sm:py-2"
              >
                <Lightbulb size={18} />
                Ver insights
              </button>
            </div>
          )}

          <InsightsModal
            isOpen={insightsAberto}
            onClose={() => setInsightsAberto(false)}
            insights={insights}
          />

          {aba !== "meta16h" && (
            <div className="grid min-w-0 gap-3 rounded-lg border border-neutral-200 bg-neutral-50 p-3 sm:grid-cols-2 sm:p-4 lg:grid-cols-5">
              <label className="min-w-0 text-sm font-medium text-neutral-700">
                Unidade de medida
                <select
                  value={undFiltro}
                  onChange={(e) => setUndFiltro(e.target.value)}
                  className={SELECT_FILTRO_CLASS}
                >
                  <option value="">Todas as unidades</option>
                  {undOptions.map((und) => (
                    <option key={und} value={und}>
                      {und}
                    </option>
                  ))}
                </select>
              </label>
              <label className="min-w-0 text-sm font-medium text-neutral-700">
                <span className="inline-flex items-center gap-1.5">
                  <Filter className="h-3.5 w-3.5 text-neutral-400" />
                  Setor
                </span>
                <select
                  value={setorFiltro}
                  onChange={(e) =>
                    setSetorFiltro(e.target.value ? Number(e.target.value) : "")
                  }
                  className={SELECT_FILTRO_CLASS}
                >
                  <option value="">Todos os setores</option>
                  {setorOptions.map((s) => (
                    <option key={s.setorId} value={s.setorId}>
                      {s.setorNome}
                    </option>
                  ))}
                </select>
              </label>
              <label className="min-w-0 text-sm font-medium text-neutral-700">
                Usuário
                <select
                  value={usuarioFiltro}
                  onChange={(e) => {
                    const id = e.target.value ? Number(e.target.value) : "";
                    setUsuarioFiltro(id);
                    if (id !== "") setAba("usuarios");
                  }}
                  className={SELECT_FILTRO_CLASS}
                >
                  <option value="">Todos os usuários</option>
                  {usuarioOptions.map((u) => (
                    <option key={u.userId} value={u.userId}>
                      {u.userNome}
                    </option>
                  ))}
                </select>
              </label>
              <label className="min-w-0 text-sm font-medium text-neutral-700">
                Item
                <select
                  value={itemFiltro}
                  onChange={(e) =>
                    setItemFiltro(e.target.value ? Number(e.target.value) : "")
                  }
                  className={SELECT_FILTRO_CLASS}
                >
                  <option value="">Todos os itens</option>
                  {itemOptions.map((item) => (
                    <option key={item.itemId} value={item.itemId}>
                      {item.descricao}
                    </option>
                  ))}
                </select>
              </label>
              <div className="flex items-end sm:col-span-2 lg:col-span-1">
                <button
                  type="button"
                  onClick={limparFiltros}
                  disabled={!temFiltroIndividual}
                  className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md border border-neutral-300 bg-white px-3 text-sm font-semibold text-neutral-700 disabled:opacity-50 md:h-auto md:py-2"
                >
                  <RotateCcw className="h-4 w-4" />
                  Limpar filtros
                </button>
              </div>
            </div>
          )}

          <div className="-mx-3 overflow-x-auto overscroll-x-contain px-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:mx-0 lg:overflow-visible lg:px-0">
            <div className="flex w-max gap-2 pb-0.5 lg:w-auto lg:flex-wrap">
              {ABAS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setAba(item.id)}
                  className={`whitespace-nowrap rounded-full px-3 py-2 text-sm font-semibold ${
                    aba === item.id
                      ? "bg-primary text-white"
                      : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {aba === "rankings" && rankings && (
            <div className="space-y-6">
              <EntregaSetorSection setores={entregaPorSetor} />
              <RelatorioTemposCharts
                usuarios={usuarios}
                setores={setores}
                usuariosMaisRapidos={rankings.usuariosMaisRapidos}
              />
              <div className="grid min-w-0 gap-4 lg:grid-cols-2">
                <RankingList
                  title="Nota da equipe"
                  hint={`Média das notas diárias em ${pontuacao.undNome} — 0 a 10`}
                  rows={rankingPontuacao}
                  empty="Sem produção cronometrada para pontuar."
                  onSelect={selecionarRanking("usuario")}
                />
                <RankingList
                  title="Menor tempo médio — usuários"
                  hint="Quem termina cada cronometragem mais rápido"
                  rows={rankings.usuariosMaisRapidos}
                  empty="Nenhum usuário com cronômetro no período."
                  onSelect={selecionarRanking("usuario")}
                />
                <RankingList
                  title="Maior produção — usuários"
                  hint="Quem encerrou mais itens no período"
                  rows={rankings.usuariosMaisProdutivos}
                  empty="Sem volume cronometrado."
                  onSelect={selecionarRanking("usuario")}
                />
                <RankingList
                  title="Melhor ritmo — usuários"
                  hint="Quantidade por hora, ranking separado por kg, un etc."
                  rows={rankings.usuariosMelhorRitmo}
                  empty="Sem quantidade comparável para ritmo."
                  onSelect={selecionarRanking("usuario")}
                />
                <RankingList
                  title="Maior produção — itens"
                  hint="Ranking de quantidade só entre itens da mesma unidade"
                  rows={rankings.itensMaiorProducao}
                  empty="Sem produção cronometrada no período."
                  onSelect={selecionarRanking("item")}
                />
                <RankingList
                  title="Itens mais rápidos"
                  hint="Menor tempo médio"
                  rows={rankings.itensMaisRapidos}
                  empty="Nenhum item com cronômetro no período."
                  onSelect={selecionarRanking("item")}
                />
                <RankingList
                  title="Itens mais lentos"
                  hint="Onde o processo mais consome tempo"
                  rows={rankings.itensMaisLentos}
                  empty="Nenhum item com cronômetro no período."
                  onSelect={selecionarRanking("item")}
                />
                <RankingList
                  title="Setor mais rápido"
                  hint="Menor tempo médio"
                  rows={rankings.setoresMaisRapidos}
                  empty="Nenhum setor com cronômetro no período."
                  onSelect={selecionarRanking("setor")}
                />
                <RankingList
                  title="Setor com maior volume"
                  hint="Mais cronometragens encerradas"
                  rows={rankings.setoresMaiorVolume}
                  empty="Sem volume por setor."
                  onSelect={selecionarRanking("setor")}
                />
                <RankingList
                  title="Maior entrega — setores"
                  hint="Itens conferidos ou não produzidos, sobre o que o setor deveria lançar nos dias de produção"
                  rows={rankings.setoresMaiorEntrega ?? []}
                  empty="Sem itens de setor no período."
                  onSelect={selecionarRanking("setor")}
                />
                <RankingList
                  title="Mais tempo pausado — usuários"
                  hint="Botão pausar ou troca de item, fora do tempo de produção"
                  rows={rankings.usuariosMaisPausados ?? []}
                  empty="Nenhuma pausa no período."
                  onSelect={selecionarRanking("usuario")}
                />
                <RankingList
                  title="Itens mais pausados"
                  hint="Onde o cronômetro ficou mais tempo pausado"
                  rows={rankings.itensMaisPausados ?? []}
                  empty="Nenhum item com pausa no período."
                  onSelect={selecionarRanking("item")}
                />
              </div>
            </div>
          )}

          {aba === "pontuacao" && (
            <PontuacaoTab
              ranking={pontuacao.ranking}
              undNome={pontuacao.undNome}
              onSelectUsuario={(userId) => {
                setUsuarioFiltro(userId);
                setAba("usuarios");
              }}
            />
          )}

          {aba === "meta16h" &&
            (assinaturas ? (
              <MetaAssinaturaTab data={assinaturas} />
            ) : (
              <p className="rounded-lg border border-neutral-200 bg-white p-6 text-center text-sm text-neutral-500">
                Não foi possível carregar a meta de assinatura 16h.
              </p>
            ))}

          {aba === "setores" && (
            <div className="space-y-6">
              <EntregaSetorSection setores={entregaPorSetor} />
              <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
                {setores.length === 0 ? (
                  <p className="px-3 py-6 text-center text-sm text-neutral-500">
                    Nenhum setor no período.
                  </p>
                ) : (
                  <>
                    <ListaMobile>
                      {setores.map((s) => (
                        <article key={s.setorId} className="px-3 py-3">
                          <p className="text-sm font-semibold text-neutral-900">
                            {s.setorNome}
                          </p>
                          <CardMetricas>
                            <CardMetrica label="Entrega">
                              {(s.itensEsperados ?? 0) > 0
                                ? `${s.percentualEntregaFormatado ?? "0%"} (${s.itensEntregues ?? 0}/${s.itensEsperados})`
                                : "—"}
                            </CardMetrica>
                            <CardMetrica label="Itens">
                              {s.itensDistintos}
                            </CardMetrica>
                            <CardMetrica label="Pessoas">
                              {s.usuariosDistintos}
                            </CardMetrica>
                            <CardMetrica label="Vezes">
                              {s.ocorrencias}
                            </CardMetrica>
                            <CardMetrica label="Total">
                              {s.tempoFormatado}
                            </CardMetrica>
                            <CardMetrica label="Pausa">
                              <span className="text-amber-800">
                                {s.tempoPausaFormatado ?? "00:00"}
                              </span>
                            </CardMetrica>
                            <CardMetrica label="Médio">
                              {s.tempoMedioFormatado}
                            </CardMetrica>
                            <CardMetrica label="Min / Máx">
                              {s.tempoMinFormatado} / {s.tempoMaxFormatado}
                            </CardMetrica>
                            <CardMetrica label="Produção" className="col-span-2">
                              <ProducaoPorUnd
                                align="start"
                                itens={
                                  undFiltro
                                    ? s.producaoPorUnd.filter(
                                        (p) =>
                                          rotuloUnd(p.undNome) === undFiltro
                                      )
                                    : s.producaoPorUnd
                                }
                              />
                            </CardMetrica>
                          </CardMetricas>
                        </article>
                      ))}
                    </ListaMobile>
                    <TabelaDesktop>
                      <table className="w-full min-w-[56rem] text-sm">
                        <thead className="bg-neutral-50">
                          <tr>
                            <th className="px-3 py-2 text-left font-semibold text-neutral-600">
                              Setor
                            </th>
                            <th className="px-3 py-2 text-right font-semibold text-neutral-600">
                              Entrega
                            </th>
                            <th className="px-3 py-2 text-right font-semibold text-neutral-600">
                              Itens
                            </th>
                            <th className="px-3 py-2 text-right font-semibold text-neutral-600">
                              Pessoas
                            </th>
                            <th className="px-3 py-2 text-right font-semibold text-neutral-600">
                              Vezes
                            </th>
                            <th className="px-3 py-2 text-right font-semibold text-neutral-600">
                              Total
                            </th>
                            <th className="px-3 py-2 text-right font-semibold text-neutral-600">
                              Pausa
                            </th>
                            <th className="px-3 py-2 text-right font-semibold text-neutral-600">
                              Médio
                            </th>
                            <th className="px-3 py-2 text-right font-semibold text-neutral-600">
                              Min / Máx
                            </th>
                            <th className="px-3 py-2 text-right font-semibold text-neutral-600">
                              Produção
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {setores.map((s) => (
                            <tr
                              key={s.setorId}
                              className="border-t border-neutral-200"
                            >
                              <td className="break-words px-3 py-2 font-medium">
                                {s.setorNome}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums">
                                {(s.itensEsperados ?? 0) > 0
                                  ? `${s.percentualEntregaFormatado ?? "0%"} (${s.itensEntregues ?? 0}/${s.itensEsperados})`
                                  : "—"}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {s.itensDistintos}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {s.usuariosDistintos}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {s.ocorrencias}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums">
                                {s.tempoFormatado}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums text-amber-800">
                                {s.tempoPausaFormatado ?? "00:00"}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums">
                                {s.tempoMedioFormatado}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums text-neutral-600">
                                {s.tempoMinFormatado} / {s.tempoMaxFormatado}
                              </td>
                              <td className="px-3 py-2 text-right">
                                <ProducaoPorUnd
                                  itens={
                                    undFiltro
                                      ? s.producaoPorUnd.filter(
                                          (p) =>
                                            rotuloUnd(p.undNome) === undFiltro
                                        )
                                      : s.producaoPorUnd
                                  }
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </TabelaDesktop>
                  </>
                )}
              </div>
            </div>
          )}

          {aba === "itens" && (
            <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
              {itens.length === 0 ? (
                <p className="px-3 py-6 text-center text-sm text-neutral-500">
                  Nenhum item com tempo no período.
                </p>
              ) : (
                <>
                  <ListaMobile>
                    {itensPorUnd.map((grupo) => (
                      <div key={grupo.undNome}>
                        <CabecalhoUndMobile
                          undNome={grupo.undNome}
                          count={grupo.items.length}
                        />
                        {grupo.items.map((item) => (
                          <article
                            key={item.itemId}
                            className="border-t border-neutral-100 px-3 py-3"
                          >
                            <p className="text-sm font-semibold text-neutral-900">
                              {item.descricao}
                            </p>
                            {item.setores.length > 0 ? (
                              <p className="mt-0.5 text-xs text-neutral-500">
                                {item.setores.join(", ")}
                              </p>
                            ) : null}
                            <CardMetricas>
                              <CardMetrica label="Vezes">
                                {item.ocorrencias}
                              </CardMetrica>
                              <CardMetrica label="Total">
                                {item.tempoFormatado}
                              </CardMetrica>
                              <CardMetrica label="Pausa">
                                <span className="text-amber-800">
                                  {item.tempoPausaFormatado ?? "00:00"}
                                </span>
                              </CardMetrica>
                              <CardMetrica label="Médio">
                                {item.tempoMedioFormatado}
                              </CardMetrica>
                              <CardMetrica label="Min / Máx">
                                {item.tempoMinFormatado} /{" "}
                                {item.tempoMaxFormatado}
                              </CardMetrica>
                              <CardMetrica label="Ritmo">
                                {item.ritmoFormatado ?? "—"}
                              </CardMetrica>
                              <CardMetrica
                                label="Produção"
                                className="col-span-2"
                              >
                                {item.producaoFormatada ?? "—"}
                                {(item.producaoPorOrigem ?? []).length > 1 ? (
                                  <p className="mt-0.5 text-[11px] font-normal text-neutral-500">
                                    {(item.producaoPorOrigem ?? [])
                                      .flatMap((origem) =>
                                        origem.producaoPorUnd.map(
                                          (p) =>
                                            `${p.total.toLocaleString("pt-BR", {
                                              maximumFractionDigits: 3,
                                            })} ${p.undNome} ${origem.lojaNome}`
                                        )
                                      )
                                      .join(" · ")}
                                  </p>
                                ) : null}
                              </CardMetrica>
                            </CardMetricas>
                          </article>
                        ))}
                      </div>
                    ))}
                  </ListaMobile>
                  <TabelaDesktop>
                    <table className="w-full min-w-[52rem] text-sm">
                      <thead className="bg-neutral-50">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold text-neutral-600">
                            Item
                          </th>
                          <th className="px-3 py-2 text-left font-semibold text-neutral-600">
                            Setor
                          </th>
                          <th className="px-3 py-2 text-right font-semibold text-neutral-600">
                            Vezes
                          </th>
                          <th className="px-3 py-2 text-right font-semibold text-neutral-600">
                            Total
                          </th>
                          <th className="px-3 py-2 text-right font-semibold text-neutral-600">
                            Pausa
                          </th>
                          <th className="px-3 py-2 text-right font-semibold text-neutral-600">
                            Médio
                          </th>
                          <th className="px-3 py-2 text-right font-semibold text-neutral-600">
                            Min / Máx
                          </th>
                          <th className="px-3 py-2 text-right font-semibold text-neutral-600">
                            Produção
                          </th>
                          <th className="px-3 py-2 text-right font-semibold text-neutral-600">
                            Ritmo
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {itensPorUnd.map((grupo) => (
                          <React.Fragment key={grupo.undNome}>
                            <CabecalhoUnd
                              undNome={grupo.undNome}
                              count={grupo.items.length}
                              colSpan={9}
                            />
                            {grupo.items.map((item) => (
                              <tr
                                key={item.itemId}
                                className="border-t border-neutral-200"
                              >
                                <td className="break-words px-3 py-2 font-medium">
                                  {item.descricao}
                                </td>
                                <td className="px-3 py-2 text-neutral-600">
                                  {item.setores.join(", ")}
                                </td>
                                <td className="px-3 py-2 text-right">
                                  {item.ocorrencias}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums">
                                  {item.tempoFormatado}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums text-amber-800">
                                  {item.tempoPausaFormatado ?? "00:00"}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums">
                                  {item.tempoMedioFormatado}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums text-neutral-600">
                                  {item.tempoMinFormatado} /{" "}
                                  {item.tempoMaxFormatado}
                                </td>
                                <td className="px-3 py-2 text-right">
                                  {item.producaoFormatada ?? "—"}
                                  {(item.producaoPorOrigem ?? []).length >
                                  1 ? (
                                    <p className="mt-0.5 text-[11px] font-normal text-neutral-500">
                                      {(item.producaoPorOrigem ?? [])
                                        .flatMap((origem) =>
                                          origem.producaoPorUnd.map(
                                            (p) =>
                                              `${p.total.toLocaleString(
                                                "pt-BR",
                                                {
                                                  maximumFractionDigits: 3,
                                                }
                                              )} ${p.undNome} ${origem.lojaNome}`
                                          )
                                        )
                                        .join(" · ")}
                                    </p>
                                  ) : null}
                                </td>
                                <td className="px-3 py-2 text-right">
                                  {item.ritmoFormatado ?? "—"}
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </TabelaDesktop>
                </>
              )}
            </div>
          )}

          {aba === "usuarios" && (
            <div className="space-y-4">
              {usuarios.map((row) => (
                <article
                  key={row.userId}
                  className="overflow-hidden rounded-lg border border-neutral-200 bg-white"
                >
                  <div className="grid gap-3 border-b border-neutral-100 bg-neutral-50 px-3 py-3 sm:grid-cols-2 sm:px-4 lg:grid-cols-6">
                    <div className="min-w-0 lg:col-span-2">
                      <p className="text-xs font-medium uppercase text-neutral-500">
                        Usuário
                      </p>
                      <p className="break-words text-base font-semibold text-neutral-900">
                        {row.userNome}
                      </p>
                      <p className="text-xs text-neutral-500">
                        {row.itensDistintos ?? (row.porItem ?? []).length} itens
                        · {row.ocorrencias} cronometragens
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase text-neutral-500">
                        Tempo de produção
                      </p>
                      <p className="text-lg font-semibold tabular-nums text-neutral-900">
                        {row.tempoFormatado}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase text-amber-800">
                        Pausado
                      </p>
                      <p className="text-lg font-semibold tabular-nums text-amber-900">
                        {row.tempoPausaFormatado ?? "00:00"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase text-neutral-500">
                        Tempo médio
                      </p>
                      <p className="text-lg font-semibold tabular-nums text-neutral-900">
                        {row.tempoMedioFormatado}
                      </p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium uppercase text-neutral-500">
                        Produção
                      </p>
                      <p className="text-sm font-semibold text-neutral-900">
                        <ProducaoPorUnd
                          align="start"
                          itens={row.producaoPorUnd}
                        />
                      </p>
                    </div>
                  </div>
                  <div className="py-2 sm:px-2">
                    <p className="mb-2 px-3 text-xs font-semibold uppercase text-neutral-500">
                      Itens produzidos
                    </p>
                    {(row.porItem ?? []).length === 0 ? (
                      <p className="px-3 py-4 text-center text-sm text-neutral-500">
                        Nenhum item cronometrado para este usuário.
                      </p>
                    ) : (
                      <>
                        <ListaMobile>
                          {groupByUndNome(
                            row.porItem ?? [],
                            (item) => item.undNome
                          ).map((grupo) => (
                            <div key={grupo.undNome}>
                              <CabecalhoUndMobile
                                undNome={grupo.undNome}
                                count={grupo.items.length}
                              />
                              {grupo.items.map((item) => (
                                <article
                                  key={item.itemId}
                                  className="px-3 py-3"
                                >
                                  <p className="text-sm font-semibold text-neutral-900">
                                    {item.descricao}
                                  </p>
                                  <p className="mt-0.5 text-xs text-neutral-500">
                                    {(item.setores ?? []).join(", ") || "—"}
                                  </p>
                                  <CardMetricas>
                                    <CardMetrica label="Vezes">
                                      {item.ocorrencias}
                                    </CardMetrica>
                                    <CardMetrica label="Tempo">
                                      {item.tempoFormatado}
                                    </CardMetrica>
                                    <CardMetrica label="Pausa">
                                      <span className="text-amber-800">
                                        {item.tempoPausaFormatado ?? "00:00"}
                                      </span>
                                    </CardMetrica>
                                    <CardMetrica label="Médio">
                                      {item.tempoMedioFormatado}
                                    </CardMetrica>
                                    <CardMetrica label="Produção">
                                      {item.producaoFormatada ?? "—"}
                                    </CardMetrica>
                                    <CardMetrica label="Ritmo">
                                      {item.ritmoFormatado ?? "—"}
                                    </CardMetrica>
                                  </CardMetricas>
                                </article>
                              ))}
                            </div>
                          ))}
                        </ListaMobile>
                        <TabelaDesktop>
                          <table className="w-full min-w-[48rem] text-sm">
                            <thead>
                              <tr className="text-neutral-500">
                                <th className="px-2 py-1.5 text-left font-medium">
                                  Item
                                </th>
                                <th className="px-2 py-1.5 text-left font-medium">
                                  Setor
                                </th>
                                <th className="px-2 py-1.5 text-right font-medium">
                                  Vezes
                                </th>
                                <th className="px-2 py-1.5 text-right font-medium">
                                  Tempo
                                </th>
                                <th className="px-2 py-1.5 text-right font-medium">
                                  Pausa
                                </th>
                                <th className="px-2 py-1.5 text-right font-medium">
                                  Médio
                                </th>
                                <th className="px-2 py-1.5 text-right font-medium">
                                  Produção
                                </th>
                                <th className="px-2 py-1.5 text-right font-medium">
                                  Ritmo
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {groupByUndNome(
                                row.porItem ?? [],
                                (item) => item.undNome
                              ).map((grupo) => (
                                <React.Fragment key={grupo.undNome}>
                                  <CabecalhoUnd
                                    undNome={grupo.undNome}
                                    count={grupo.items.length}
                                    colSpan={8}
                                  />
                                  {grupo.items.map((item) => (
                                    <tr
                                      key={item.itemId}
                                      className="border-t border-neutral-100"
                                    >
                                      <td className="break-words px-2 py-1.5 font-medium">
                                        {item.descricao}
                                      </td>
                                      <td className="px-2 py-1.5 text-neutral-600">
                                        {(item.setores ?? []).join(", ") ||
                                          "—"}
                                      </td>
                                      <td className="px-2 py-1.5 text-right">
                                        {item.ocorrencias}
                                      </td>
                                      <td className="px-2 py-1.5 text-right tabular-nums">
                                        {item.tempoFormatado}
                                      </td>
                                      <td className="px-2 py-1.5 text-right tabular-nums text-amber-800">
                                        {item.tempoPausaFormatado ?? "00:00"}
                                      </td>
                                      <td className="px-2 py-1.5 text-right tabular-nums">
                                        {item.tempoMedioFormatado}
                                      </td>
                                      <td className="px-2 py-1.5 text-right">
                                        {item.producaoFormatada ?? "—"}
                                      </td>
                                      <td className="px-2 py-1.5 text-right">
                                        {item.ritmoFormatado ?? "—"}
                                      </td>
                                    </tr>
                                  ))}
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        </TabelaDesktop>
                      </>
                    )}
                  </div>
                </article>
              ))}
              {usuarios.length === 0 && (
                <div className="rounded-lg border border-neutral-200 bg-white px-3 py-6 text-center text-sm text-neutral-500">
                  Nenhum usuário com tempo no período para os filtros.
                </div>
              )}
            </div>
          )}

          {aba === "detalhe" && (
            <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
              {linhas.length === 0 ? (
                <p className="px-3 py-6 text-center text-sm text-neutral-500">
                  Nenhum tempo encerrado no período para os filtros.
                </p>
              ) : (
                <>
                  <ListaMobile>
                    {linhasPorUnd.map((grupo) => (
                      <div key={grupo.undNome}>
                        <CabecalhoUndMobile
                          undNome={grupo.undNome}
                          count={grupo.items.length}
                          singular="lançamento"
                          plural="lançamentos"
                        />
                        {grupo.items.map((linha) => (
                          <article
                            key={`${linha.periodoKey}-${linha.setorId}-${linha.itemId}-${linha.userId ?? 0}`}
                            className="px-3 py-3"
                          >
                            <p className="text-sm font-semibold text-neutral-900">
                              {linha.descricao}
                            </p>
                            <p className="mt-0.5 text-xs text-neutral-500">
                              {linha.periodoLabel}
                              {linha.setorNome ? ` · ${linha.setorNome}` : ""}
                              {linha.userNome ? ` · ${linha.userNome}` : ""}
                            </p>
                            <CardMetricas>
                              <CardMetrica label="Vezes">
                                {linha.ocorrencias}
                              </CardMetrica>
                              <CardMetrica label="Total">
                                {linha.tempoFormatado}
                              </CardMetrica>
                              <CardMetrica label="Pausa">
                                <span className="text-amber-800">
                                  {linha.tempoPausaFormatado ?? "00:00"}
                                </span>
                              </CardMetrica>
                              <CardMetrica label="Médio">
                                {linha.tempoMedioFormatado}
                              </CardMetrica>
                              <CardMetrica
                                label="Min / Máx"
                                className="col-span-2"
                              >
                                {linha.tempoMinFormatado ?? "—"} /{" "}
                                {linha.tempoMaxFormatado ?? "—"}
                              </CardMetrica>
                            </CardMetricas>
                          </article>
                        ))}
                      </div>
                    ))}
                  </ListaMobile>
                  <TabelaDesktop>
                    <table className="w-full min-w-[56rem] text-sm">
                      <thead className="bg-neutral-50">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold text-neutral-600">
                            Período
                          </th>
                          <th className="px-3 py-2 text-left font-semibold text-neutral-600">
                            Setor
                          </th>
                          <th className="px-3 py-2 text-left font-semibold text-neutral-600">
                            Usuário
                          </th>
                          <th className="px-3 py-2 text-left font-semibold text-neutral-600">
                            Item
                          </th>
                          <th className="px-3 py-2 text-right font-semibold text-neutral-600">
                            Vezes
                          </th>
                          <th className="px-3 py-2 text-right font-semibold text-neutral-600">
                            Total
                          </th>
                          <th className="px-3 py-2 text-right font-semibold text-neutral-600">
                            Pausa
                          </th>
                          <th className="px-3 py-2 text-right font-semibold text-neutral-600">
                            Médio
                          </th>
                          <th className="px-3 py-2 text-right font-semibold text-neutral-600">
                            Min / Máx
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {linhasPorUnd.map((grupo) => (
                          <React.Fragment key={grupo.undNome}>
                            <CabecalhoUnd
                              undNome={grupo.undNome}
                              count={grupo.items.length}
                              colSpan={9}
                              singular="lançamento"
                              plural="lançamentos"
                            />
                            {grupo.items.map((linha) => (
                              <tr
                                key={`${linha.periodoKey}-${linha.setorId}-${linha.itemId}-${linha.userId ?? 0}`}
                                className="border-t border-neutral-200"
                              >
                                <td className="break-words px-3 py-2">
                                  {linha.periodoLabel}
                                </td>
                                <td className="px-3 py-2">{linha.setorNome}</td>
                                <td className="px-3 py-2">
                                  {linha.userNome ?? "—"}
                                </td>
                                <td className="break-words px-3 py-2">
                                  {linha.descricao}
                                </td>
                                <td className="px-3 py-2 text-right">
                                  {linha.ocorrencias}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums">
                                  {linha.tempoFormatado}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums text-amber-800">
                                  {linha.tempoPausaFormatado ?? "00:00"}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums">
                                  {linha.tempoMedioFormatado}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums text-neutral-600">
                                  {linha.tempoMinFormatado ?? "—"} /{" "}
                                  {linha.tempoMaxFormatado ?? "—"}
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </TabelaDesktop>
                </>
              )}
            </div>
          )}

          {aba === "pausas" && (
            <div className="rounded-lg border border-neutral-200 bg-white p-4">
              {loadingPausas ? (
                <p className="text-center text-neutral-500">
                  Carregando pausas...
                </p>
              ) : pausas.length === 0 ? (
                <p className="text-center text-neutral-500">
                  Nenhuma pausa no período.
                </p>
              ) : (
                (() => {
                  const usuarios = Array.from(
                    new Set(pausas.map((p) => p.usuarioNome))
                  );
                  const itensUnicos = Array.from(
                    new Set(pausas.map((p) => p.itemDescricao))
                  );
                  const LIMIAR_LONGA_MS = 5 * 60 * 1000;
                  const POR_PAGINA = 10;

                  const filtered = pausas.filter((p) => {
                    if (
                      pausaFiltroUsuario &&
                      p.usuarioNome !== pausaFiltroUsuario
                    )
                      return false;
                    if (pausaFiltroItem && p.itemDescricao !== pausaFiltroItem)
                      return false;
                    if (pausaFiltroLongas && p.retomadoEm) {
                      const diff =
                        new Date(p.retomadoEm).getTime() -
                        new Date(p.pausadoEm).getTime();
                      if (diff < LIMIAR_LONGA_MS) return false;
                    }
                    if (pausaFiltroLongas && !p.retomadoEm) return true;
                    return true;
                  });

                  const totalPaginas = Math.ceil(filtered.length / POR_PAGINA);
                  const paginaAtual = Math.min(
                    pausaPagina,
                    Math.max(totalPaginas - 1, 0)
                  );
                  const paginados = filtered.slice(
                    paginaAtual * POR_PAGINA,
                    (paginaAtual + 1) * POR_PAGINA
                  );

                  return (
                    <>
                      <div className="mb-4 rounded-lg bg-neutral-50 p-3">
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-4">
                          <div>
                            <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
                              Usuário
                            </label>
                            <select
                              value={pausaFiltroUsuario}
                              onChange={(e) => {
                                setPausaFiltroUsuario(e.target.value);
                                setPausaPagina(0);
                              }}
                              className="h-11 w-full rounded-md border border-neutral-200 bg-white px-2.5 text-base shadow-sm focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400 md:h-auto md:py-2 md:text-xs"
                            >
                              <option value="">Todos</option>
                              {usuarios.map((u) => (
                                <option key={u} value={u}>
                                  {u}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
                              Item
                            </label>
                            <select
                              value={pausaFiltroItem}
                              onChange={(e) => {
                                setPausaFiltroItem(e.target.value);
                                setPausaPagina(0);
                              }}
                              className="h-11 w-full rounded-md border border-neutral-200 bg-white px-2.5 text-base shadow-sm focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400 md:h-auto md:py-2 md:text-xs"
                            >
                              <option value="">Todos</option>
                              {itensUnicos.map((i) => (
                                <option key={i} value={i}>
                                  {i}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="flex items-end">
                            <label className="flex cursor-pointer items-center gap-2 rounded-md border border-neutral-200 bg-white px-3 py-2 text-xs shadow-sm transition hover:border-amber-300">
                              <input
                                type="checkbox"
                                checked={pausaFiltroLongas}
                                onChange={(e) => {
                                  setPausaFiltroLongas(e.target.checked);
                                  setPausaPagina(0);
                                }}
                                className="h-4 w-4 rounded border-neutral-300 text-amber-600 focus:ring-amber-500"
                              />
                              <span className="font-medium text-neutral-700">
                                Só longas (5min+)
                              </span>
                            </label>
                          </div>
                          <div className="flex items-end">
                            <p className="text-xs text-neutral-400">
                              {filtered.length} pausa
                              {filtered.length !== 1 ? "s" : ""}
                            </p>
                          </div>
                        </div>
                      </div>
                      {filtered.length === 0 ? (
                        <p className="text-center text-sm text-neutral-400">
                          Nenhuma pausa com esses filtros.
                        </p>
                      ) : (
                        <>
                          <ListaMobile className="-mx-4 border-t border-neutral-100">
                            {paginados.map((p, idx) => {
                              const duracao = p.retomadoEm
                                ? (() => {
                                    const diffMs =
                                      new Date(p.retomadoEm).getTime() -
                                      new Date(p.pausadoEm).getTime();
                                    const totalSec = Math.floor(diffMs / 1000);
                                    const min = Math.floor(totalSec / 60);
                                    const sec = totalSec % 60;
                                    return min > 0
                                      ? `${min}min ${sec}s`
                                      : `${sec}s`;
                                  })()
                                : "Em aberto";
                              const isLonga =
                                p.retomadoEm &&
                                new Date(p.retomadoEm).getTime() -
                                  new Date(p.pausadoEm).getTime() >=
                                  LIMIAR_LONGA_MS;
                              return (
                                <article
                                  key={idx}
                                  className={`px-3 py-3 ${isLonga ? "bg-amber-50" : ""}`}
                                >
                                  <p className="text-sm font-semibold text-neutral-900">
                                    {p.itemDescricao}
                                  </p>
                                  <p className="mt-0.5 text-xs text-neutral-500">
                                    {p.usuarioNome}
                                  </p>
                                  <CardMetricas>
                                    <CardMetrica label="Data">
                                      {(p as PausaPeriodoItem).dataFicha
                                        ? new Date(
                                            (p as PausaPeriodoItem).dataFicha +
                                              "T12:00:00"
                                          ).toLocaleDateString("pt-BR")
                                        : "—"}
                                    </CardMetrica>
                                    <CardMetrica label="Duração">
                                      <span
                                        className={
                                          isLonga
                                            ? "text-amber-700"
                                            : undefined
                                        }
                                      >
                                        {duracao}
                                      </span>
                                    </CardMetrica>
                                    <CardMetrica
                                      label="Horário"
                                      className="col-span-2"
                                    >
                                      {new Date(
                                        p.pausadoEm
                                      ).toLocaleTimeString("pt-BR", {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })}
                                      {p.retomadoEm &&
                                        ` → ${new Date(p.retomadoEm).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`}
                                    </CardMetrica>
                                    <CardMetrica
                                      label="Justificativa"
                                      className="col-span-2"
                                    >
                                      {p.justificativa || (
                                        <span className="italic font-normal text-neutral-400">
                                          —
                                        </span>
                                      )}
                                    </CardMetrica>
                                  </CardMetricas>
                                </article>
                              );
                            })}
                          </ListaMobile>
                          <TabelaDesktop>
                            <table className="w-full min-w-[48rem] text-sm">
                              <thead className="bg-neutral-50">
                                <tr>
                                  <th className="px-3 py-2 text-left font-semibold text-neutral-600">
                                    Data
                                  </th>
                                  <th className="px-3 py-2 text-left font-semibold text-neutral-600">
                                    Item
                                  </th>
                                  <th className="px-3 py-2 text-left font-semibold text-neutral-600">
                                    Usuário
                                  </th>
                                  <th className="px-3 py-2 text-left font-semibold text-neutral-600">
                                    Horário
                                  </th>
                                  <th className="px-3 py-2 text-right font-semibold text-neutral-600">
                                    Duração
                                  </th>
                                  <th className="px-3 py-2 text-left font-semibold text-neutral-600">
                                    Justificativa
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-neutral-100">
                                {paginados.map((p, idx) => {
                                  const duracao = p.retomadoEm
                                    ? (() => {
                                        const diffMs =
                                          new Date(p.retomadoEm).getTime() -
                                          new Date(p.pausadoEm).getTime();
                                        const totalSec = Math.floor(
                                          diffMs / 1000
                                        );
                                        const min = Math.floor(totalSec / 60);
                                        const sec = totalSec % 60;
                                        return min > 0
                                          ? `${min}min ${sec}s`
                                          : `${sec}s`;
                                      })()
                                    : "Em aberto";
                                  const isLonga =
                                    p.retomadoEm &&
                                    new Date(p.retomadoEm).getTime() -
                                      new Date(p.pausadoEm).getTime() >=
                                      LIMIAR_LONGA_MS;
                                  return (
                                    <tr
                                      key={idx}
                                      className={isLonga ? "bg-amber-50" : ""}
                                    >
                                      <td className="px-3 py-2 text-xs text-neutral-600">
                                        {(p as PausaPeriodoItem).dataFicha
                                          ? new Date(
                                              (p as PausaPeriodoItem)
                                                .dataFicha + "T12:00:00"
                                            ).toLocaleDateString("pt-BR")
                                          : "—"}
                                      </td>
                                      <td className="px-3 py-2 text-xs font-medium text-neutral-800">
                                        {p.itemDescricao}
                                      </td>
                                      <td className="px-3 py-2 text-xs text-neutral-600">
                                        {p.usuarioNome}
                                      </td>
                                      <td className="px-3 py-2 text-xs text-neutral-600">
                                        {new Date(
                                          p.pausadoEm
                                        ).toLocaleTimeString("pt-BR", {
                                          hour: "2-digit",
                                          minute: "2-digit",
                                        })}
                                        {p.retomadoEm &&
                                          ` → ${new Date(p.retomadoEm).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`}
                                      </td>
                                      <td
                                        className={`px-3 py-2 text-right text-xs font-semibold ${isLonga ? "text-amber-700" : "text-neutral-600"}`}
                                      >
                                        {duracao}
                                      </td>
                                      <td className="px-3 py-2 text-xs text-neutral-600">
                                        {p.justificativa || (
                                          <span className="italic text-neutral-400">
                                            —
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </TabelaDesktop>
                          {totalPaginas > 1 && (
                            <div className="mt-3 flex items-center justify-center gap-1">
                              <button
                                type="button"
                                disabled={paginaAtual === 0}
                                onClick={() => setPausaPagina((pg) => pg - 1)}
                                className="h-11 min-w-[5.5rem] rounded-md px-2.5 text-sm font-medium text-neutral-600 transition hover:bg-neutral-100 disabled:opacity-40 lg:h-auto lg:py-1.5 lg:text-xs"
                              >
                                ← Anterior
                              </button>
                              <span className="px-3 text-xs text-neutral-500">
                                {paginaAtual + 1} / {totalPaginas}
                              </span>
                              <button
                                type="button"
                                disabled={paginaAtual >= totalPaginas - 1}
                                onClick={() => setPausaPagina((pg) => pg + 1)}
                                className="h-11 min-w-[5.5rem] rounded-md px-2.5 text-sm font-medium text-neutral-600 transition hover:bg-neutral-100 disabled:opacity-40 lg:h-auto lg:py-1.5 lg:text-xs"
                              >
                                Próxima →
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </>
                  );
                })()
              )}
            </div>
          )}
        </div>
      )}
    </main>
  );
};

export default ControleProducaoRelatorioTemposPage;
